import React from 'react';
import classNames from 'classnames';
import { Stage } from '../Home';
import { PROCESS_CONFIG, ProgressItem } from '../progressConfig';
import styles from './ProgressBar.module.scss';
import { StageCounts } from './StageProgressBreakdown';
import { Inline } from 'components/layout';

type ProgressBarSectionProps = {
  width: number;
  variant: 'done' | 'inProgress' | 'conflicts' | 'notStarted' | 'empty';
};

const ProgressBarSection = ({ width, variant }: ProgressBarSectionProps) => {
  const classes = classNames(styles.progressBarSection, styles[variant]);

  return <div className={classes} style={{ flexGrow: width }} />;
};

type ProgressBarSectionsProps = {
  config: readonly ProgressItem[];
  counts: StageCounts;
  total: number;
};

const ProgressBarSections = ({
  config,
  counts,
  total,
}: ProgressBarSectionsProps) => {
  return (
    <>
      {config.map(({ key, variant }) => {
        const value = counts[key] ?? 0;
        const width = (value / total) * 100;

        return (
          width > 0 && (
            <ProgressBarSection key={variant} variant={variant} width={width} />
          )
        );
      })}
    </>
  );
};

export type ProgressBarProps = {
  complete?: number;
  partiallyComplete: number;
  consensusRequired?: number;
  notStarted: number;
  stage: Stage;
} & StageCounts;

export const ProgressBar = ({ stage, ...counts }: ProgressBarProps) => {
  const config = PROCESS_CONFIG[stage === 'included' ? 'included' : 'default'];
  const total = Object.values(counts).reduce((sum, value) => {
    return sum + (value ?? 0);
  }, 0);

  return (
    <Inline
      className={styles.progressBar}
      aria-label="Progress"
      role="progressbar"
    >
      {total === 0 ? (
        <ProgressBarSection variant="empty" width={100} />
      ) : (
        <ProgressBarSections config={config} counts={counts} total={total} />
      )}
    </Inline>
  );
};
